import api from '../api';
import { saveAs } from '../../utils/saveFile';
import {
  ApproveRequest,
  RejectRequest,
  RemediationAction,
  RemediationActionCreate,
  RemediationActionUpdate,
  RiskAcceptance,
  RiskAcceptanceCreate,
  ScorePreviewRequest,
  ScorePreviewResponse,
  TprmDashboardResponse,
  TransitionResponse,
  Vendor,
  VendorCreate,
  VendorDocument,
  VendorEvidence,
  VendorEvidenceCreate,
  VendorEvidenceUpdate,
  VendorImportResult,
  VendorListFilters,
  VendorOffboardingTask,
  VendorOffboardingTaskUpdate,
  VendorIncident,
  VendorIncidentCreate,
  VendorIncidentUpdate,
  VendorPerformanceReview,
  VendorPerformanceReviewCreate,
  VendorUpdate,
} from '../../types/compliance';

// NOTE: the shared axios instance (`services/api.ts`) is configured with
// baseURL = config.API_URL, which already includes the `/api/v1` prefix.
// Paths here are relative to that prefix.
const BASE = '/compliance/tprm';

export const tprmService = {
  // --- Vendor CRUD ---------------------------------------------------------
  async listVendors(filters?: VendorListFilters): Promise<Vendor[]> {
    const { data } = await api.get<Vendor[]>(`${BASE}/vendors`, {
      params: filters,
    });
    return data;
  },

  async getVendor(vendorId: string): Promise<Vendor> {
    const { data } = await api.get<Vendor>(`${BASE}/vendors/${vendorId}`);
    return data;
  },

  async createVendor(body: VendorCreate): Promise<Vendor> {
    const { data } = await api.post<Vendor>(`${BASE}/vendors`, body);
    return data;
  },

  async updateVendor(vendorId: string, body: VendorUpdate): Promise<Vendor> {
    const { data } = await api.patch<Vendor>(
      `${BASE}/vendors/${vendorId}`,
      body,
    );
    return data;
  },

  async deleteVendor(vendorId: string): Promise<void> {
    await api.delete(`${BASE}/vendors/${vendorId}`);
  },

  // --- Workflow transitions ------------------------------------------------
  async submit(vendorId: string): Promise<TransitionResponse> {
    const { data } = await api.post<TransitionResponse>(
      `${BASE}/vendors/${vendorId}/submit`,
      {},
    );
    return data;
  },

  async startReview(
    vendorId: string,
    notes?: string | null,
  ): Promise<TransitionResponse> {
    const { data } = await api.post<TransitionResponse>(
      `${BASE}/vendors/${vendorId}/review`,
      { notes: notes ?? null },
    );
    return data;
  },

  async approve(
    vendorId: string,
    body: ApproveRequest = {},
  ): Promise<TransitionResponse> {
    const { data } = await api.post<TransitionResponse>(
      `${BASE}/vendors/${vendorId}/approve`,
      body,
    );
    return data;
  },

  async reject(
    vendorId: string,
    body: RejectRequest,
  ): Promise<TransitionResponse> {
    const { data } = await api.post<TransitionResponse>(
      `${BASE}/vendors/${vendorId}/reject`,
      body,
    );
    return data;
  },

  async rescore(vendorId: string): Promise<Vendor> {
    const { data } = await api.post<Vendor>(
      `${BASE}/vendors/${vendorId}/rescore`,
      {},
    );
    return data;
  },

  // --- Import --------------------------------------------------------------
  async importVendor(
    file: File,
    thirdPartyId?: string,
  ): Promise<VendorImportResult> {
    const form = new FormData();
    form.append('file', file);
    const params = thirdPartyId ? { third_party_id: thirdPartyId } : undefined;
    const { data } = await api.post<VendorImportResult>(
      `${BASE}/vendors/import`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      },
    );
    return data;
  },

  async downloadTemplate(): Promise<Blob> {
    const response = await api.get(`${BASE}/vendors/import-template`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // --- Score preview -------------------------------------------------------
  async previewScore(
    body: ScorePreviewRequest,
  ): Promise<ScorePreviewResponse> {
    const { data } = await api.post<ScorePreviewResponse>(
      `${BASE}/score-preview`,
      body,
    );
    return data;
  },

  // --- Dashboard -----------------------------------------------------------
  async dashboard(): Promise<TprmDashboardResponse> {
    const { data } = await api.get<TprmDashboardResponse>(`${BASE}/dashboard`);
    return data;
  },

  // --- Remediations (Phase 4b) ---------------------------------------------
  async listRemediations(vendorId: string): Promise<RemediationAction[]> {
    const { data } = await api.get<RemediationAction[]>(
      `${BASE}/vendors/${vendorId}/remediations`,
    );
    return data;
  },

  async createRemediation(
    vendorId: string,
    body: RemediationActionCreate,
  ): Promise<RemediationAction> {
    const { data } = await api.post<RemediationAction>(
      `${BASE}/vendors/${vendorId}/remediations`,
      body,
    );
    return data;
  },

  async updateRemediation(
    vendorId: string,
    remediationId: string,
    body: RemediationActionUpdate,
  ): Promise<RemediationAction> {
    const { data } = await api.patch<RemediationAction>(
      `${BASE}/vendors/${vendorId}/remediations/${remediationId}`,
      body,
    );
    return data;
  },

  async deleteRemediation(
    vendorId: string,
    remediationId: string,
  ): Promise<void> {
    await api.delete(
      `${BASE}/vendors/${vendorId}/remediations/${remediationId}`,
    );
  },

  // --- Risk Acceptances (Phase 4b) -----------------------------------------
  async listRiskAcceptances(vendorId: string): Promise<RiskAcceptance[]> {
    const { data } = await api.get<RiskAcceptance[]>(
      `${BASE}/vendors/${vendorId}/risk-acceptances`,
    );
    return data;
  },

  async createRiskAcceptance(
    vendorId: string,
    body: RiskAcceptanceCreate,
  ): Promise<RiskAcceptance> {
    const { data } = await api.post<RiskAcceptance>(
      `${BASE}/vendors/${vendorId}/risk-acceptances`,
      body,
    );
    return data;
  },

  async deactivateRiskAcceptance(
    vendorId: string,
    acceptanceId: string,
  ): Promise<RiskAcceptance> {
    const { data } = await api.post<RiskAcceptance>(
      `${BASE}/vendors/${vendorId}/risk-acceptances/${acceptanceId}/deactivate`,
      {},
    );
    return data;
  },

  // --- Evidence (Phase 4b) -------------------------------------------------
  async listEvidence(vendorId: string): Promise<VendorEvidence[]> {
    const { data } = await api.get<VendorEvidence[]>(
      `${BASE}/vendors/${vendorId}/evidence`,
    );
    return data;
  },

  async createEvidence(
    vendorId: string,
    body: VendorEvidenceCreate,
  ): Promise<VendorEvidence> {
    const { data } = await api.post<VendorEvidence>(
      `${BASE}/vendors/${vendorId}/evidence`,
      body,
    );
    return data;
  },

  async updateEvidence(
    vendorId: string,
    evidenceId: string,
    body: VendorEvidenceUpdate,
  ): Promise<VendorEvidence> {
    const { data } = await api.patch<VendorEvidence>(
      `${BASE}/vendors/${vendorId}/evidence/${evidenceId}`,
      body,
    );
    return data;
  },

  async deleteEvidence(vendorId: string, evidenceId: string): Promise<void> {
    await api.delete(`${BASE}/vendors/${vendorId}/evidence/${evidenceId}`);
  },

  // --- Vendor Documents (Phase 4b) -----------------------------------------
  async listVendorDocuments(vendorId: string): Promise<VendorDocument[]> {
    const { data } = await api.get<VendorDocument[]>(
      `${BASE}/vendors/${vendorId}/documents`,
    );
    return data;
  },

  async uploadEvidenceDocument(
    vendorId: string,
    evidenceId: string,
    file: File,
    description?: string,
  ): Promise<VendorEvidence> {
    const form = new FormData();
    form.append('file', file);
    form.append('description', description || '');
    const { data } = await api.post<VendorEvidence>(
      `${BASE}/vendors/${vendorId}/evidence/${evidenceId}/document`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async downloadVendorDocument(
    vendorId: string,
    documentId: string,
  ): Promise<Blob> {
    const response = await api.get(
      `${BASE}/vendors/${vendorId}/documents/${documentId}/download`,
      { responseType: 'blob' },
    );
    const contentType =
      (response.headers && (response.headers as any)['content-type']) ||
      'application/octet-stream';
    return new Blob([response.data], { type: contentType });
  },

  async deleteVendorDocument(
    vendorId: string,
    documentId: string,
  ): Promise<void> {
    await api.delete(`${BASE}/vendors/${vendorId}/documents/${documentId}`);
  },

  // --- Offboarding (Phase 6c) ---------------------------------------------
  async archiveVendor(vendorId: string): Promise<TransitionResponse> {
    const { data } = await api.post<TransitionResponse>(
      `${BASE}/vendors/${vendorId}/archive`,
    );
    return data;
  },

  async listOffboardingTasks(vendorId: string): Promise<VendorOffboardingTask[]> {
    const { data } = await api.get<VendorOffboardingTask[]>(
      `${BASE}/vendors/${vendorId}/offboarding`,
    );
    return data;
  },

  async updateOffboardingTask(
    vendorId: string,
    taskId: string,
    body: VendorOffboardingTaskUpdate,
  ): Promise<VendorOffboardingTask> {
    const { data } = await api.patch<VendorOffboardingTask>(
      `${BASE}/vendors/${vendorId}/offboarding/${taskId}`,
      body,
    );
    return data;
  },

  // --- CSV export ---------------------------------------------------------
  async exportVendorsCsv(filters?: VendorListFilters): Promise<void> {
    const today = new Date().toISOString().slice(0, 10);
    await saveAs(`tprm-vendors-${today}.csv`, async () => {
      const response = await api.get(`${BASE}/vendors/export.csv`, {
        params: filters,
        responseType: 'blob',
      });
      return new Blob([response.data], { type: 'text/csv' });
    });
  },

  // --- Performance Reviews (Phase 6e) -------------------------------------
  async listPerformanceReviews(
    vendorId: string,
  ): Promise<VendorPerformanceReview[]> {
    const { data } = await api.get<VendorPerformanceReview[]>(
      `${BASE}/vendors/${vendorId}/performance-reviews`,
    );
    return data;
  },

  async createPerformanceReview(
    vendorId: string,
    body: VendorPerformanceReviewCreate,
  ): Promise<VendorPerformanceReview> {
    const { data } = await api.post<VendorPerformanceReview>(
      `${BASE}/vendors/${vendorId}/performance-reviews`,
      body,
    );
    return data;
  },

  // --- Incidents (Phase 6f) -----------------------------------------------
  async listIncidents(
    vendorId: string,
    opts?: { onlyOpen?: boolean },
  ): Promise<VendorIncident[]> {
    const { data } = await api.get<VendorIncident[]>(
      `${BASE}/vendors/${vendorId}/incidents`,
      { params: opts?.onlyOpen ? { only_open: true } : undefined },
    );
    return data;
  },

  async createIncident(
    vendorId: string,
    body: VendorIncidentCreate,
  ): Promise<VendorIncident> {
    const { data } = await api.post<VendorIncident>(
      `${BASE}/vendors/${vendorId}/incidents`,
      body,
    );
    return data;
  },

  async updateIncident(
    vendorId: string,
    incidentId: string,
    body: VendorIncidentUpdate,
  ): Promise<VendorIncident> {
    const { data } = await api.patch<VendorIncident>(
      `${BASE}/vendors/${vendorId}/incidents/${incidentId}`,
      body,
    );
    return data;
  },
};

export default tprmService;
